/** @file MIRROR_ASYMMETRY warnings (§5.8): unfixed mirrored directions that show one-sided parts. */
import type { Doc } from './schema';
import type { DirectionEntry, DirectionsDoc } from './schemaParts/animation';
import type { AttachmentDoc } from './schemaParts/attachments';
import type { Warning } from '../warning';
import { mirrorName } from './mirrorName';
import { names } from '../names';
import { makeError } from '../pose2dError';

type Entries = DirectionsDoc['entries'];

/** True for a mirrorFix entry whose fix has content (a skin, a draw-order op or an animation). */
function hasFix(e: DirectionEntry): boolean {
  const fix = e.mode === 'mirrorFix' ? e.fix : undefined;
  if (!fix) return false;
  return fix.skin !== undefined || (fix.drawOrderOps ?? []).length > 0 || Object.keys(fix.animations ?? {}).length > 0;
}

/** A part swaps sides under a flip when it is mounted or its attachment or slot name ends in -L/-R. */
const swapsSides = (att: AttachmentDoc, slotName: string | undefined): boolean =>
  att.mount !== undefined || mirrorName(att.name) !== null || (slotName !== undefined && mirrorName(slotName) !== null);

/** Names of the attachments a mirrored direction can show that swap sides; fix-only skins are skipped. */
function sidedAttachments(doc: Doc, entries: Entries): string[] {
  const fixSkins = new Set(names.DIRS.map((d) => entries[d]?.fix?.skin));
  const out = new Map<string, string>();
  for (const [skinId, skin] of Object.entries(doc.skins)) {
    if (fixSkins.has(skinId)) continue;
    for (const e of skin.entries) {
      const att = doc.attachments[e.attachment];
      if (att && swapsSides(att, doc.slots[e.slot]?.name)) out.set(e.attachment, att.name);
    }
  }
  return [...out.values()];
}

/** One MIRROR_ASYMMETRY warning per (mirrored direction without a fix, attachment that would swap sides). */
export function mirrorAsymmetry(doc: Doc): Warning[] {
  const entries: Entries = doc.directions?.entries ?? {};
  const sided = sidedAttachments(doc, entries);
  const out: Warning[] = [];
  for (const dir of names.DIRS) {
    const e = entries[dir];
    if (!e || e.mode === 'authored' || hasFix(e)) continue;
    const source = e.source ?? '?';
    for (const attachment of sided) out.push(makeError('MIRROR_ASYMMETRY', { dir, source, attachment }, { path: `directions.entries.${dir}` }));
  }
  return out;
}
